import React, { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AuthContext } from "../../../context/authcontext";
import { EntityNotificationService } from "./entityNotification.service";
import { useTenantRole } from "../../../components/TenantRoleDisplay";

const MySwal = withReactContent(Swal);

interface Interval {
  interval_id: number;
  interval_key: string;
  interval_display_name: string;
}

interface EntityNotificationType {
  entity_notification_type_id: number;
  entity_notification_type_key: string;
  entity_notification_type_display_name: string;
}

interface EntityNotificationResponse {
  entity_notification_id: number;
  tenant_name: string;
  tenant_logo: string;
  created_by_full_name: string;
  updated_by_full_name: string;
  notification_type_display_name: string;
  interval_before_duration: number;
  related_entity_name: string;
  is_active: boolean;
  created_datetime: string | null;
  updated_datetime: string | null;
  interval: Interval;
  tenant_id: number;
  entity_notification_type: EntityNotificationType;
  created_by: number;
  updated_by: number;
}

interface NotificationSetting {
  id: number | string;
  interval_before_duration: number;
  interval_id: number;
  entity_notification_type_id: number;
  enabled: boolean;
  isNew?: boolean;
}

const ExpirationNotificationSettings = () => {
  const { selectedTenantId, auth } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);
  const isSalesUser = userRole?.role_key === "SALESUSER";
  const [notifications, setNotifications] = useState<NotificationSetting[]>([]);
  const [originalNotifications, setOriginalNotifications] = useState<
    NotificationSetting[]
  >([]);

  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [notificationTypes, setNotificationTypes] = useState<
    EntityNotificationType[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchIntervals = async () => {
      try {
        const data = await EntityNotificationService.fetchIntervals();
        setIntervals(data);
      } catch (error) {
        console.error("Error loading intervals", error);
      }
    };

    fetchIntervals();
  }, []);

  useEffect(() => {
    const fetchNotificationTypes = async () => {
      try {
        const data = await EntityNotificationService.fetchNotificationTypes();
        setNotificationTypes(data);
      } catch (error) {
        console.error("Error loading notification types", error);
      }
    };

    fetchNotificationTypes();
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return;

    const fetchEntityNotifications = async () => {
      setIsLoading(true);
      try {
        const results = await EntityNotificationService.fetchEntityNotifications(selectedTenantId, "tenant_subscription");

        const subNotifications = results
          .map((item: EntityNotificationResponse) => ({
            id: item.entity_notification_id,
            interval_before_duration: item.interval_before_duration,
            interval_id: item.interval.interval_id,
            entity_notification_type_id:
              item.entity_notification_type.entity_notification_type_id,
            enabled: item.is_active,
          }));

        setNotifications(subNotifications);
        setOriginalNotifications(JSON.parse(JSON.stringify(subNotifications)));
      } catch (error) {
        console.error("Error loading entity notifications", error);
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load notification settings.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntityNotifications();
  }, [selectedTenantId]);

  const filteredIntervals = intervals.filter((i) =>
    ["WEEK", "MONTH", "DAY"].includes(i.interval_key.toUpperCase())
  );

  const handleAddNotification = () => {
    const newNotification: NotificationSetting = {
      id: `temp_${Date.now()}`,
      interval_before_duration: 10,
      interval_id:
        filteredIntervals.find((i) => i.interval_key.toUpperCase() === "DAY")?.interval_id ||
        filteredIntervals[0]?.interval_id ||
        1,
      entity_notification_type_id:
        notificationTypes.find(
          (t) => t.entity_notification_type_key === "EMAIL"
        )?.entity_notification_type_id ||
        notificationTypes[0]?.entity_notification_type_id ||
        1,
      enabled: true,
      isNew: true,
    };
    setNotifications([...notifications, newNotification]);
  };

  const handleDeleteNotification = (id: number | string) => {
    if (notifications.length <= 1) {
      MySwal.fire({
        icon: "warning",
        title: "Cannot Delete",
        text: "At least one notification setting is required.",
        confirmButtonText: "OK",
      });
      return;
    }
    setNotifications(notifications.filter((n) => n.id !== id));
  };

  const handleUpdateNotification = (
    id: number | string,
    field: keyof NotificationSetting,
    value: any
  ) => {
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, [field]: value } : n))
    );
  };

  const hasChanges = () => {
    return (
      JSON.stringify(notifications) !== JSON.stringify(originalNotifications)
    );
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      MySwal.fire({
        icon: "info",
        title: "No changes detected",
        text: "You didn't update anything.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!selectedTenantId) {
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "No tenant selected.",
      });
      return;
    }

    MySwal.fire({
      title: "Saving…",
      allowOutsideClick: false,
      didOpen: () => MySwal.showLoading(),
    });

    setIsSaving(true);

    try {
      const deletedNotifications = originalNotifications.filter(
        (orig) => !notifications.find((n) => n.id === orig.id)
      );

      const newNotifications = notifications.filter((n) => n.isNew);

      const updatedNotifications = notifications.filter((n) => {
        if (n.isNew) return false;
        const original = originalNotifications.find((o) => o.id === n.id);
        return original && JSON.stringify(original) !== JSON.stringify(n);
      });

      for (const notification of deletedNotifications) {
        await EntityNotificationService.deleteEntityNotification(notification.id);
      }

      for (const notification of newNotifications) {
        await EntityNotificationService.createEntityNotification({
          tenant_id: selectedTenantId,
          interval_before_duration: notification.interval_before_duration,
          interval_id: notification.interval_id,
          entity_notification_type_id: notification.entity_notification_type_id,
          related_entity_name: "tenant_subscription",
          is_active: notification.enabled,
          created_by: auth?.user_id,
          updated_by: auth?.user_id,
        });
      }

      for (const notification of updatedNotifications) {
        await EntityNotificationService.updateEntityNotification(notification.id, {
          interval_before_duration: notification.interval_before_duration,
          interval_id: notification.interval_id,
          entity_notification_type_id: notification.entity_notification_type_id,
          is_active: notification.enabled,
          updated_by: auth?.user_id,
          tenant_id: selectedTenantId,
        });
      }

      const results = await EntityNotificationService.fetchEntityNotifications(selectedTenantId, "tenant_subscription");
      const subNotifications = results
        .map((item: EntityNotificationResponse) => ({
          id: item.entity_notification_id,
          interval_before_duration: item.interval_before_duration,
          interval_id: item.interval.interval_id,
          entity_notification_type_id:
            item.entity_notification_type.entity_notification_type_id,
          enabled: item.is_active,
        }));
      setNotifications(subNotifications);
      setOriginalNotifications(JSON.parse(JSON.stringify(subNotifications)));

      MySwal.close();
      MySwal.fire({
        icon: "success",
        title: "Saved!",
        text: "Your notification settings have been saved.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error saving notifications:", error);
      MySwal.close();
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save notification settings. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!hasChanges()) return;

    const result = await MySwal.fire({
      icon: "warning",
      title: "Discard Changes?",
      text: "Are you sure you want to cancel your changes?",
      showCancelButton: true,
      confirmButtonText: "Yes, discard",
      cancelButtonText: "No, keep editing",
      reverseButtons: true,
      focusCancel: true,
    });

    if (result.isConfirmed) {
      setNotifications(JSON.parse(JSON.stringify(originalNotifications)));
      MySwal.fire({
        icon: "info",
        title: "Changes discarded",
        text: "Your unsaved changes have been reset.",
        timer: 2000,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timerProgressBar: true,
      });
    }
  };

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [notifications, originalNotifications]);

  const getIntervalDisplayName = (intervalId: number): string => {
    return (
      intervals.find((i) => i.interval_id === intervalId)
        ?.interval_display_name || "Unknown"
    );
  };

  const getNotificationTypeDisplayName = (typeId: number): string => {
    return (
      notificationTypes.find((t) => t.entity_notification_type_id === typeId)
        ?.entity_notification_type_display_name || "Unknown"
    );
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "200px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="border-bottom mb-3 pb-3">
          <h5 className="fw-semibold mb-1">Notification Preferences</h5>
          <p className="text-muted">
            Configure when and how you want to be notified about your tenant subscription expiration
          </p>
        </div>

        <div className="mb-4">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="border rounded p-3 mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <div className="row align-items-center">
                <div className="col-md-2">
                  <label className="form-label">Duration</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    value={notification.interval_before_duration}
                    onChange={(e) =>
                      handleUpdateNotification(
                        notification.id,
                        "interval_before_duration",
                        parseInt(e.target.value) || 0
                      )
                    }
                    disabled={isSalesUser}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Interval</label>
                  <select
                    className="form-select"
                    value={notification.interval_id}
                    onChange={(e) =>
                      handleUpdateNotification(
                        notification.id,
                        "interval_id",
                        parseInt(e.target.value)
                      )
                    }
                    disabled={isSalesUser}
                  >
                    {filteredIntervals.map((interval) => (
                      <option
                        key={interval.interval_id}
                        value={interval.interval_id}
                      >
                        {interval.interval_display_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Channel</label>
                  <select
                    className="form-select"
                    value={notification.entity_notification_type_id}
                    onChange={(e) =>
                      handleUpdateNotification(
                        notification.id,
                        "entity_notification_type_id",
                        parseInt(e.target.value)
                      )
                    }
                    disabled={isSalesUser}
                  >
                    {notificationTypes.map((type) => (
                      <option
                        key={type.entity_notification_type_id}
                        value={type.entity_notification_type_id}
                      >
                        {type.entity_notification_type_display_name}
                      </option>
                    ))}
                  </select>
                </div>

                {!isSalesUser && (
                  <div className="col-md-2 text-end">
                    <label className="form-label d-block">&nbsp;</label>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDeleteNotification(notification.id)}
                      title={
                        notifications.length <= 1
                          ? "At least one notification is required"
                          : "Delete notification"
                      }
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="row mt-2">
                <div className="col-12">
                  {notification.interval_before_duration > 0 && (
                    <small className="text-muted">
                      <i className="ti ti-bell me-1" />
                      Notify{" "}
                      {notification.interval_before_duration}{" "}
                      {getIntervalDisplayName(
                        notification.interval_id
                      ).toLowerCase()}{" "}
                      before tenant subscription expires via{" "}
                      {getNotificationTypeDisplayName(
                        notification.entity_notification_type_id
                      )}
                    </small>
                  ) || <small className="text-muted text-danger">
                      <i className="ti ti-bell me-1" />
                      Enter duration value to receive notification on time.
                    </small>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {!isSalesUser && (
          <div className="mb-4">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAddNotification}
              disabled={filteredIntervals.length === 0 || notificationTypes.length === 0}
            >
              <i className="fa fa-plus me-1" />
              Add Notification
            </button>
          </div>
        )}

        {!isSalesUser && (
          <div className="border-top pt-3">
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || !hasChanges() || notifications.some((notification) => notification.interval_before_duration <= 0)}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpirationNotificationSettings;
